/**
 * The three marks this dialog draws that the app does not already own: the person inside the form's
 * first field, and the confirmation's card arrow and camera.
 *
 * Everything else on both faces is imported rather than re-drawn — `docs/design/README.md`'s rule is
 * one drawing per concept, and a second plan document or a second ruler at a different stroke weight
 * is how a trainer meets two of each:
 *
 * | The mock's mark | What is used |
 * |---|---|
 * | the e-mail field's envelope | `shared/ui/AuthIcons`' `MailIcon` |
 * | the plan card's document | `shared/ui/EmptyStateIcons`' `PlanMarkIcon` |
 * | the measurements card's ruler | `shared/ui/EmptyStateIcons`' `MeasureMarkIcon` |
 * | the tick, on the disc and in the timeline | `shared/ui/AuthIcons`' `CheckIcon` |
 *
 * All three are DECORATIVE: each field has its own `<label>`, and the card beside each of the other
 * two names itself in words.
 */

interface GlyphProps {
  readonly className?: string;
}

/** The mock's stroke: 2 on a 24 box, round joins, no fill — `EmptyStateIcons`' own, so the set matches. */
const STROKE_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * The head-and-shoulders inside the first field's box. The mock draws it on „Imię" and a mail on
 * „E-mail", and NOTHING on „Nazwisko"; that asymmetry is the specimen's and is reproduced rather
 * than tidied — the second field is the continuation of the first, not a new subject.
 *
 * `app/ui/NavIcons` has the same silhouette for the Klienci item and cannot be imported: `app/` is
 * above `features/` and the import would be upward. It is one drawing either way, and if a third
 * surface ever wants it the pair goes to `shared/ui` together.
 */
export function PersonIcon({ className }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

/**
 * The card's trailing arrow. Deliberately NOT `ChevronRightIcon`: a chevron says "there is more of
 * this" and the mock's arrow says "this goes somewhere", which is exactly what these three cards do.
 */
export function ArrowRightIcon({ className }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

/** A camera body with its lens wedge — the "poproś o wideo" card, which is not built yet. */
export function VideoIcon({ className }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="3" y="6" width="13" height="12" rx="3" />
      <path d="m16 10 5-3v10l-5-3" />
    </svg>
  );
}
